"use client";

import { currentChallengeWeekKey, nextChallengeWeekKey } from "@dailywars/core/engine";
import { useEffect, useState } from "react";

const DAY_SECONDS = 24 * 3600;
const COUNTDOWN_PLACEHOLDER = "--:--:--";

function getMsUntilNextUtcMidnight(now: Date): number {
  const nextMidnight = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1);
  return nextMidnight - now.getTime();
}

export function getMsUntilNextChallengeWeek(now: Date): number {
  const nextWeekKey = nextChallengeWeekKey(currentChallengeWeekKey(now));
  return Date.parse(`${nextWeekKey}T00:00:00.000Z`) - now.getTime();
}

export function formatUtcCountdown(ms: number): string {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

export function formatChallengeCountdown(ms: number): string {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  if (totalSeconds >= DAY_SECONDS) {
    const totalHours = Math.floor(totalSeconds / 3600);
    const days = Math.floor(totalHours / 24);
    const hours = totalHours % 24;
    return `${days}d ${hours}h`;
  }
  return formatUtcCountdown(ms);
}

function useTickingCountdown({
  getRemainingMs,
  format,
}: {
  getRemainingMs: (now: Date) => number;
  format: (ms: number) => string;
}): string {
  const [countdown, setCountdown] = useState(COUNTDOWN_PLACEHOLDER);

  useEffect(() => {
    const update = () => {
      const next = format(getRemainingMs(new Date()));
      setCountdown((current) => (current === next ? current : next));
    };

    update();
    const intervalId = setInterval(update, 1000);
    return () => clearInterval(intervalId);
  }, [format, getRemainingMs]);

  return countdown;
}

export function useUtcMidnightCountdown(): string {
  return useTickingCountdown({
    getRemainingMs: getMsUntilNextUtcMidnight,
    format: formatUtcCountdown,
  });
}

export function useNextChallengeCountdown(): string {
  return useTickingCountdown({
    getRemainingMs: getMsUntilNextChallengeWeek,
    format: formatChallengeCountdown,
  });
}
